<template>
  <div
    class="container plan-cards"
    :class="{
      'plan-cards__secondary': isSecondary,
    }"
  >
    <div class="subscription-box__title-box">
      <h1 class="subscription-box__title">
        {{ billing.subscriptionBoxTitle }}
      </h1>
    </div>

    <p
      v-if="trialByPlanV2_18369Treatment()"
      class="plan-cards__info plan-cards__info--top"
      v-html="billing.subscriptionBoxInfo"
    />

    <div class="plan-cards__cards">
      <PlanCard
        :plan="annualPlan"
        :billing="billing"
        :selected="selectedPlan.id === 'plan_annual'"
        class="plan-cards__card--annual"
        @click="updateSelectedPlan('plan_annual')"
      />
      <PlanCard
        :plan="monthlyPlan"
        :billing="billing"
        :selected="selectedPlan.id === 'plan_monthly'"
        @click="updateSelectedPlan('plan_monthly')"
      />
    </div>

    <div class="plan-cards__button-box">
      <BillingSubscribeButton
        :billing="billing"
        :plan="selectedPlan"
        :location="isSecondary ? 'top' : 'bottom'"
      />
    </div>

    <p v-if="isAndroidApp" class="plan-cards__info plan-cards__info--android">
      The free trial will start unless you had a subscription in the past in
      which case the subscription will start immediately. Please check the
      details on the popup that will appear after clicking the button.<template
        v-if="!hardGate20516Active"
      >
        You can get a taste of Shortform right now with our free summaries of
        <router-link
          :to="{
            name: 'book.preview',
            params: { url_slug: 'deep-work' },
          }"
          >Deep Work</router-link
        >
        and
        <router-link
          :to="{
            name: 'book.preview',
            params: {
              url_slug: 'how-to-win-friends-and-influence-people',
            },
          }"
          >How to Win Friends and Influence People</router-link
        >.
      </template>
    </p>

    <p
      v-if="!trialByPlanV2_18369Treatment()"
      class="plan-cards__info plan-cards__info--regular"
    >
      {{ billing.subscriptionBoxInfo }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type {
  AppStoreBillingView,
  BillingView,
  PlanView,
} from '@/models/billing'

import PlanCard from './PlanCard.vue'
import BillingSubscribeButton from '@/app/components/billing/BillingSubscribeButton.vue'
import { computed, onMounted, ref } from 'vue'
import type { Raw } from 'vue'
import { platform } from '@/services/platform'
import { trialByPlanV2_18369Treatment } from '@/services/ab/18369_trial_by_plan'
import { assertNotNull } from '@/helpers/typing'
import { useHardGateBilling20516Active } from '@/app/composables/useHardGateBilling20516'

const props = defineProps<{
  billing: BillingView
  isSecondary?: boolean
}>()

const selectedPlan = ref<Raw<PlanView>>(props.billing.annualPlan)

const updateSelectedPlan = (planId: string): void => {
  selectedPlan.value =
    planId === 'plan_annual'
      ? props.billing.annualPlan
      : props.billing.monthlyPlan
}

const isAndroidApp = ref(false)
const hardGate20516Active = useHardGateBilling20516Active()

onMounted(async () => {
  // On the Android native app, we show text informing users of the
  // free content they can access, so that we do not violate the
  // Subscriptions policy. See issue #10818.
  // Variant B of exp_hard_gate_billing_20516 blocks those book routes,
  // so the free-summary links are left off.
  isAndroidApp.value = await platform.isNativeAndroidApp()
})

const annualPlan = computed(() => {
  if (props.billing.showExternalPayment16218) {
    const billing = props.billing as AppStoreBillingView
    return assertNotNull(billing.stripeAnnualPlan16218)
  }

  return props.billing.annualPlan
})

const monthlyPlan = computed(() => {
  if (props.billing.showExternalPayment16218) {
    const billing = props.billing as AppStoreBillingView
    return assertNotNull(billing.stripeMonthlyPlan16218)
  }

  return props.billing.monthlyPlan
})
</script>

<style scoped lang="scss">
.plan-cards {
  padding-block: 48px;
  margin-bottom: 48px;
  border-bottom: 1px solid $c-black-13;

  @media (max-width: $w-mobile-s) {
    margin-bottom: 32px;
  }

  &__cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 42px;
    @media (max-width: $w-mobile-s) {
      gap: 12px;
      margin-top: 38px;
    }
  }

  &__button-box {
    display: flex;
    justify-content: center;
    margin-top: 32px;
    @media (max-width: $w-mobile-s) {
      width: 100%;
    }

    @media (max-width: $w-tablet-s) {
      flex-direction: column;
      gap: 12px;
      align-items: center;
    }

    :deep(.subscribe-button) {
      justify-content: center;
      align-items: center;
      border-radius: 53px;
      background: $c-orange;
      color: $c-white;
      font-size: 18px;
      font-weight: 400;
      width: 256px;
      line-height: 22px;
      padding-block: 8px;
      @media (max-width: $w-mobile-s) {
        width: 100%;
      }

      @include hover {
        background: $c-orange-2;
      }
    }

    :deep(.subscription-info__text) {
      font-size: 14px;
      line-height: 24px;
      margin-bottom: 12px;
    }

    :deep(.subscribe-button__wrapper) {
      display: flex;
      max-width: 547px;
      text-align: center;
      flex-direction: column;
      align-items: center;
      @media (max-width: $w-mobile-s) {
        flex-grow: 1;
      }
    }
  }

  &__info {
    color: $c-black-3;
    text-align: center;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.28px;
    margin-top: 20px;
    max-width: 547px;
    margin-inline: auto;

    &--top {
      margin-top: 12px;
      margin-bottom: 27px;
    }

    a {
      color: $c-blue-4;
      text-decoration: underline;
    }

    @media (max-width: $w-mobile-s) {
      padding-inline: 10px;
      line-height: 22px;
    }

    @media (max-width: $w-mobile-s) {
      padding-inline: 10px;
      line-height: 22px;
    }
  }

  &__secondary {
    border-bottom: none;
    background: rgba(56, 118, 240, 0.08);
    border-radius: 20px;

    @media (max-width: $w-max-content-width) {
      margin-inline: var(--container-padding);
      max-width: calc(100% - (var(--container-padding) * 2));
    }
  }
}

.subscription-box__title {
  @media (max-width: $w-mobile-s) {
    font-size: 28px;
  }

  &-box {
    margin-bottom: 12px;
  }
}

.night-mode {
  .plan-cards {
    border-color: $c-dark-5;
  }
}
</style>
